/**
 * OrderDisplayPage - Customer-facing display showing ready orders
 * Designed for large screens at pickup counter
 * Features:
 * - Large order numbers with status
 * - Ready orders prominently displayed
 * - Preparing orders with progress
 * - Auto-refresh
 * - Dark/Light themes optimized for visibility
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Coffee, Check, Clock, ChefHat, Bell, Volume2, RefreshCw
} from 'lucide-react';
import { api } from '@/utils/apiClient';

// Order status card component
const OrderCard = ({ order, isReady }) => {
  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div 
      className={`rounded-3xl p-6 transition-all duration-500 ${
        isReady 
          ? 'bg-green-500 text-white animate-pulse shadow-2xl shadow-green-500/30' 
          : 'bg-white/10 text-white/80'
      }`}
      data-testid={`display-order-${order.order_number || order.id || 'unknown'}`}
    >
      <div className="text-center">
        <div className={`text-7xl font-black mb-2 ${isReady ? 'text-white' : 'text-white/90'}`}>
          {order.order_number || '—'}
        </div>
        <div className={`text-lg font-medium ${isReady ? 'text-green-100' : 'text-white/60'}`}>
          {order.customer_name || 'Guest'}
        </div>
        {isReady ? (
          <div className="mt-4 flex items-center justify-center gap-2 text-xl font-bold">
            <Check className="w-6 h-6" />
            READY
          </div>
        ) : (
          <div className="mt-4 flex items-center justify-center gap-2 text-white/50">
            <ChefHat className="w-5 h-5 animate-bounce" />
            <span>Preparing</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default function OrderDisplayPage() {
  const [readyOrders, setReadyOrders] = useState([]);
  const [preparingOrders, setPreparingOrders] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [connected, setConnected] = useState(true);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get('/api/pickup/orders');
      setReadyOrders(res.data.ready || []);
      setPreparingOrders(res.data.preparing || []);
      setLastUpdate(new Date());
      setConnected(true);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setConnected(false);
    }
  }, []);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Play sound on new ready order (optional - would need audio file)
  useEffect(() => {
    if (readyOrders.length > 0) {
      // Could play a chime sound here
    }
  }, [readyOrders.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden" data-testid="order-display-page">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-sm px-8 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-teal-500 rounded-2xl flex items-center justify-center">
            <Coffee className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Order Status</h1>
            <p className="text-white/50">Pick up when your number is called</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          {/* Connection Status */}
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
            connected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400'} ${connected ? 'animate-pulse' : ''}`} />
            {connected ? 'Live' : 'Reconnecting...'}
          </div>
          
          {/* Last Update */}
          <div className="text-white/40 text-sm">
            Updated: {lastUpdate.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8">
        {/* Ready Orders Section - Prominent */}
        {readyOrders.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Bell className="w-8 h-8 text-green-400 animate-bounce" />
              <h2 className="text-4xl font-bold text-green-400">Ready for Pickup!</h2>
              <div className="ml-auto px-6 py-2 bg-green-500 text-white text-2xl font-bold rounded-full">
                {readyOrders.length}
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {readyOrders.slice(0, 10).map(order => (
                <OrderCard key={order.id} order={order} isReady={true} />
              ))}
            </div>
          </div>
        )}

        {/* No Ready Orders */}
        {readyOrders.length === 0 && (
          <div className="mb-12 text-center py-16">
            <Coffee className="w-24 h-24 text-white/20 mx-auto mb-6" />
            <h2 className="text-3xl font-medium text-white/40">No orders ready yet</h2>
            <p className="text-white/30 mt-2">Your order number will appear here when ready</p>
          </div>
        )}

        {/* Preparing Orders Section */}
        {preparingOrders.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <ChefHat className="w-6 h-6 text-amber-400" />
              <h2 className="text-2xl font-medium text-white/70">Now Preparing</h2>
              <div className="ml-auto px-4 py-1 bg-amber-500/20 text-amber-400 font-medium rounded-full">
                {preparingOrders.length}
              </div>
            </div>
            
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
              {preparingOrders.slice(0, 16).map(order => (
                <OrderCard key={order.id} order={order} isReady={false} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer - Current Time */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/50 backdrop-blur px-8 py-4 flex items-center justify-between border-t border-white/10">
        <div className="text-white/30 text-sm">
          Please have your receipt ready • Orders called by number
        </div>
        <div className="text-6xl font-light text-white/80 tabular-nums">
          {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      {/* Auto-updating clock */}
      <ClockUpdater />
    </div>
  );
}

// Simple component to force re-render every minute for clock
function ClockUpdater() {
  const [, setTick] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);
  
  return null;
}
